<template>
  <v-card>
    <RemoveAliasDialog ref="removeAliasDialog" />
    <v-card-title class="py-2">
      <v-icon class="mr-2">merge_type</v-icon>
      <div class="title info--text">Aliases</div>
      <div class="title pl-1"> ⋅ </div>
      <div class="caption pl-1"> {{ aliases.length }} {{ pluralize('alias', aliases.length, 'es') }}</div>
    </v-card-title>
    <v-divider />
    <template v-if="editForm">
      <v-card-actions>
        <AddAliasDialog />
      </v-card-actions>
      <v-divider />
    </template>
    <v-card-text>
      <p>
        <strong>Aliases</strong> for this data source DO NOT store a subset of data. These aliases can be moved or removed.
      </p>
      <p>
        <strong>Name aliases</strong> are alternative names for this data source.
      </p>
    </v-card-text>
    <v-data-table hide-actions :items="nameAliases" no-data-text="No name aliases" hide-headers>
      <template v-slot:items="{ item }">
        <td class="text-xs-left">
          <v-chip label small>
            {{ item.Name }}
          </v-chip>
        </td>
        <td class="text-xs-right">
          <span v-if="item.IsPrimary" class="caption" :class="[matchesLocationName(item) ? 'info--text' : 'warning--text']">
            Primary
            <Tooltip :text="makeTooltipText(item)" small />
          </span>
          <div v-show="editForm && !item.IsPrimary">
            <v-btn v-if="isSystemAdmin" @click="makePrimary(item)" :loading="item.makePrimaryLoading" color="info" small>
              <v-icon class="mr-2">admin_panel_settings</v-icon>
              <span class="text--capitalize">
                Make primary
              </span>
            </v-btn>
            <v-tooltip :open-delay="500" left>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" @click="removeAlias(item)" icon>
                  <v-icon color="error">clear</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Button.Remove") }}</span>
            </v-tooltip>
          </div>
        </td>
      </template>
    </v-data-table>
    <v-divider />
  </v-card>
</template>

<script>
  import AddAliasDialog from './AddAliasDialog'
  import RemoveAliasDialog from './RemoveAliasDialog'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import {
    createNamespacedHelpers,
  } from 'vuex'

  import {
    formatDepthHeightValues,
  } from '@/helpers/depthHeight'

  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')
  const synonyms = createNamespacedHelpers('synonyms')
  const authenticate = createNamespacedHelpers('authenticate')

  import {
    ALIAS_TYPES,
  } from '@/helpers/synonyms'

  import {
    pluralize,
  } from '@/helpers/strings'

  export default {
    components: {
      AddAliasDialog,
      RemoveAliasDialog,
    },
    props: {
      editForm: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      ALIAS_TYPES,
      headers: [{
        text: 'Alias Name',
        value: 'Name',
        sortable: false,
      },
      {
        text: 'Alias Type',
        value: 'type',
        sortable: false,
      },
      {
        text: 'Details',
        value: 'details',
        sortable: false,
      },
      {
        text: '',
        value: 'action',
        sortable: false,
      },
      ],
    }),
    computed: {
      ...monitoringFeatures.mapGetters(['currentFeatureDataSourceIndexByType', 'currentMonitoringFeature']),
      ...dataSources.mapGetters(['dataSourceIndex']),
      ...authenticate.mapGetters(['isSystemAdmin']),
      continuousDataSource() {
        return this.currentFeatureDataSourceIndexByType[SOURCE_TYPES.continuousAquarius]
      },
      continuousStation() {
        if (!this.continuousDataSource) return []
        return this.dataSourceIndex[SOURCE_TYPES.continuousAquarius][this.continuousDataSource.SourceId]
      },
      dataSourceAliases() {
        return this.continuousStation && this.continuousStation.Synonyms || []
      },
      nameAliases() {
        return this.dataSourceAliases.filter(x => !x.IsQaQc && !x.IsDepthHeightDatum)
      },
      aliases() {
        let aliases = this.nameAliases.map(alias => ({
          ...alias,
          type: ALIAS_TYPES.alias,
        }))

        let result = [...aliases]
        return result
      },
    },
    methods: {
      ...synonyms.mapActions(['updateSynonym']),
      ...dataSources.mapActions(['fetchDataSources']),
      formatDepthHeightValues,
      pluralize,
      makeTooltipText(primaryAlias) {
        let hasMatchingPrimaryAlias = primaryAlias.Name == this.currentMonitoringFeature.DisplayName
        let nonPrimaryText =
          '<br/><div><strong>Note:</strong> The primary alias is currently different than the location name.</div>'
        let result = '<div>The primary name shows up in reports for this data source.</div>'
        if (!hasMatchingPrimaryAlias) result += nonPrimaryText

        return result
      },
      async removeAlias(alias) {
        this.$refs.removeAliasDialog.show(alias)
      },
      matchesLocationName(alias) {
        return alias.Name == this.currentMonitoringFeature.DisplayName
      },
      async makePrimary(alias) {
        this.$set(alias, 'makePrimaryLoading', true)

        try {
          let payload = {
            synonymId: alias.Id,
            isPrimary: true,
            synonymName: alias.Name,
            sourceType: SOURCE_TYPES.continuousAquarius,
          }

          await this.updateSynonym(payload)
          await this.fetchDataSources({
            sourceTypes: [SOURCE_TYPES.continuousAquarius],
          })
        } catch (error) {
          console.error(error)
        }

        this.$set(alias, 'makePrimaryLoading', false)
      },
    },
  }
</script>